export function formatTimeOnly(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

// Lifecycle stages tracked on every shot. Used by both the FDC firing data table and
// the Gun Commander history so they stay in lockstep.
export const SHOT_TIMELINE_STAGES = [
  { key: "created_at", label: "Created" },
  { key: "confirmed_at", label: "Sent" },
  { key: "acknowledged_at", label: "Roger" },
  { key: "gun_ready_at", label: "Gun Ready" },
  { key: "shot_complete_at", label: "Shot" },
  { key: "rounds_complete_at", label: "Rounds Complete" },
];

// Pick the most recent valid timestamp on a shot across all lifecycle stages.
// Returns { key, label, t (epoch ms), iso } or null if nothing is set.
export function latestStageOf(shot) {
  let latest = null;
  for (const s of SHOT_TIMELINE_STAGES) {
    const ts = shot?.[s.key];
    if (!ts) continue;
    const t = new Date(ts).getTime();
    if (!Number.isFinite(t)) continue;
    if (!latest || t > latest.t) latest = { ...s, t, iso: ts };
  }
  return latest;
}

export const DEFAULT_EXPANDED = {
  selectMission: true,
  missionDetails: false,
  battery: false,
  fireReg: false,
  startMission: false,
  shots: false,
  manualObs: false,
  unproc: false,
  proc: false,
  admin: false,
};

export const getMissionOpenState = (hasMission) =>
  hasMission
    ? {
        ...DEFAULT_EXPANDED,
        selectMission: false,
        missionDetails: true,
      }
    : DEFAULT_EXPANDED;

export const getSinglePanelState = (panel) => ({
  selectMission: false,
  missionDetails: false,
  battery: false,
  fireReg: false,
  startMission: false,
  shots: false,
  manualObs: false,
  unproc: false,
  proc: false,
  admin: false,
  [panel]: true,
});

export const parsePtl = (value) => {
  if (value === "" || value === null || value === undefined) return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n;
};

export const buildMgrsString = (zone, rawEasting, rawNorthing) => {
  const eastingStr = String(Math.round(parseFloat(rawEasting))).padStart(
    5,
    "0",
  );
  const northingStr = String(Math.round(parseFloat(rawNorthing))).padStart(
    5,
    "0",
  );
  return `${zone}${eastingStr}${northingStr}`;
};

export const parseMgrsSimple = (mgrsString) => {
  if (!mgrsString || mgrsString.length < 15) return null;
  const zone = mgrsString.slice(0, 5);
  const eStr = mgrsString.slice(5, 10);
  const nStr = mgrsString.slice(10, 15);
  const e = Number(eStr);
  const n = Number(nStr);
  if (!Number.isFinite(e) || !Number.isFinite(n)) return null;
  return { zone, e, n };
};

export const clamp5 = (v) => {
  const r = Math.round(v);
  if (!Number.isFinite(r)) return null;
  if (r < 0 || r > 99999) return null;
  return r;
};

export const milsToRad = (m) => (m * 2 * Math.PI) / 6400;
export const radToMils = (r) => ((r * 6400) / (2 * Math.PI) + 6400) % 6400;
export const azimuthMilsFromVector = (dx, dy) => radToMils(Math.atan2(dx, dy));
export const dist = (dx, dy) => Math.hypot(dx, dy);
export const f0 = (v) => (Number.isFinite(v) ? String(Math.round(v)) : "-");
export const formatMeters = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return `${Math.round(n)} m`;
};

export const formatSignedMeters = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  const rounded = Math.round(n);
  if (rounded === 0) return "0 m";
  return `${rounded > 0 ? "+" : ""}${rounded} m`;
};

export const deriveObservationMetrics = (obs, targetMgrs) => {
  const parsed = parseMgrsSimple(targetMgrs);
  const observedE = Number(obs?.observed_x);
  const observedN = Number(obs?.observed_y);

  if (!parsed || !Number.isFinite(observedE) || !Number.isFinite(observedN)) {
    return {
      canCompute: false,
      deltaE: null,
      deltaN: null,
      distance: null,
    };
  }

  const deltaE = observedE - parsed.e;
  const deltaN = observedN - parsed.n;

  return {
    canCompute: true,
    deltaE,
    deltaN,
    distance: Math.hypot(deltaE, deltaN),
  };
};
export const extractEffectiveTargetMgrs = (data) => {
  if (!data) return "";
  const candidates = [
    data.effective_mgrs_target,
    data.effective_target_mgrs,
    data.corrected_mgrs_target,
    data.corrected_target_mgrs,
  ].filter(Boolean);

  if (candidates.length) return String(candidates[0]);
  if (typeof data.corrected_target === "string") return data.corrected_target;

  if (data.corrected_target && typeof data.corrected_target === "object") {
    if (data.corrected_target.mgrs) return String(data.corrected_target.mgrs);
    if (data.corrected_target.mgrs_target)
      return String(data.corrected_target.mgrs_target);
  }
  return "";
};
